// Tedalytics UI port of the independently owned media analytics templates.
// The content/navigation structure is preserved; Homeframe owns the persistent shell.
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  AppScrollView,
  AppShell,
  AppViewport,
  HomeframeProvider,
  HomeframeRouterProvider,
  HomeframeInput,
  HomeframeSelect,
  NavLink,
  Link,
  RouterOutlet,
  createHomeframeRouter,
  useRouteScrollRestoration,
  useRouterSnapshot,
} from "@builtbyted/homeframe";
import { api, passkey, setCsrf } from "./api";
import icon from "../brand/icon.png";
import {
  useData,
  useQuery,
  scoped,
  useLink,
  num,
  str,
  count,
  duration,
  date,
  shortDate,
  timeAgo,
  Page,
  Skeleton,
  Message,
  Section,
  Poster,
  DayPicker,
  Metric,
  TopList,
} from "./ui";
import { Dashboard, Insights } from "./Dashboard";
import { ViewerMatches } from "./ViewerMatches";
import { Media } from "./Media";
import { clearDataCache } from "./data-cache";
import { useTheme, setTheme, type Theme } from "./theme";

type Row = Record<string, unknown>;
interface Server {
  id: number;
  name: string;
  kind: string;
  status: string;
  stream_count: number;
  history_count: number;
  last_success: number | null;
  last_error: string | null;
  url: string;
  enabled: number;
}
interface Auth {
  authenticated: boolean;
  enrolled: boolean;
  csrf: string | null;
}
interface Stats {
  plays: number;
  duration: number;
  users: number;
  movies: Row[];
  shows: Row[];
  users_top: Row[];
  platforms: Row[];
  daily: Row[];
  decisions: Row[];
}
const SessionContext = createContext<{
  auth: Auth | null;
  refresh: () => void;
}>({ auth: null, refresh: () => {} });
function navigateQuery(values: Record<string, string>, pathname?: string) {
  const url = new URL(window.location.href);
  if (pathname) url.pathname = pathname;
  for (const [k, v] of Object.entries(values)) {
    if (v) url.searchParams.set(k, v);
    else url.searchParams.delete(k);
  }
  void router.navigate(url.pathname + url.search + url.hash);
}
const routes = [
  { id: "home", path: "/", element: <Dashboard /> },
  { id: "activity", path: "/activity", element: <Home /> },
  { id: "home-alias", path: "/home", element: <Dashboard /> },
  { id: "history", path: "/history", element: <History /> },
  { id: "history-detail", path: "/history/:id", element: <HistoryDetail /> },
  { id: "users", path: "/users", element: <Users /> },
  { id: "libraries", path: "/libraries", element: <Libraries /> },
  { id: "graphs", path: "/graphs", element: <Insights /> },
  { id: "media", path: "/media", element: <Media /> },
  { id: "settings", path: "/settings", element: <Settings /> },
  { id: "logs", path: "/logs", element: <Logs /> },
  { id: "signup", path: "/signup", element: <Dashboard /> },
  {
    id: "not-found",
    path: "*",
    element: (
      <Page title="Page not found">
        <Link to="/">Go home</Link>
      </Page>
    ),
  },
];
const router = createHomeframeRouter(routes);

export function App() {
  const state = useData<Auth>("/auth/state");
  useEffect(() => {
    setCsrf(state.data?.csrf || "");
  }, [state.data]);
  return (
    <HomeframeProvider>
      <HomeframeRouterProvider router={router}>
        <SessionContext.Provider
          value={{ auth: state.data, refresh: state.refresh }}
        >
          <Shell error={state.error} />
        </SessionContext.Provider>
      </HomeframeRouterProvider>
    </HomeframeProvider>
  );
}
function Shell({ error }: { error: string }) {
  const { auth, refresh } = useContext(SessionContext);
  const { scrollKey, direction, scrollBehavior, permalinkScroll } =
    useRouteScrollRestoration();
  return (
    <AppViewport>
      <AppShell
        header={<Header />}
        headerOverlay
        bottomPlacement="overlay"
        bottom={auth?.authenticated ? <Navigation mobile /> : undefined}
      >
        <AppScrollView
          className="page-scroll"
          tabIndex={0}
          aria-label="Page content"
          scrollKey={scrollKey}
          navigationType={direction}
          scrollBehavior={scrollBehavior}
          permalinkScroll={permalinkScroll}
        >
          {error ? (
            <Message error>
              {error} <button onClick={refresh}>Retry connection</button>
            </Message>
          ) : !auth ? (
            <div className="page">
              <Skeleton kind="page" />
            </div>
          ) : auth.authenticated ? (
            <RouterOutlet />
          ) : (
            <SignIn />
          )}
        </AppScrollView>
      </AppShell>
    </AppViewport>
  );
}
const nav = [
  ["/", "Dashboard", "◈"],
  ["/libraries", "Libraries", "▤"],
  ["/users", "Users", "♟"],
  ["/history", "History", "◷"],
  ["/graphs", "Insights", "▥"],
];
function NavIcon({ name }: { name: string }) {
  const paths: Record<string, ReactNode> = {
    Dashboard: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </>
    ),
    Libraries: (
      <>
        <rect x="3" y="4" width="4" height="16" rx="1" />
        <rect x="10" y="4" width="4" height="16" rx="1" />
        <path d="m17 5 4-1 3 15-4 1z" transform="translate(-2 0)" />
      </>
    ),
    Users: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 21v-3a6 6 0 0 1 12 0v3M16 5a3 3 0 0 1 0 6M21 21v-3a6 6 0 0 0-3-5.2" />
      </>
    ),
    History: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    Insights: (
      <>
        <path d="M4 20V5m0 15h16M8 16v-4m5 4V7m5 9v-7" />
      </>
    ),
    Settings: (
      <>
        <path
          d="m9 3-.7 2.2-2 .9-2.2-.5-1.5 2.6L4 10v2l-1.4 1.8 1.5 2.6 2.2-.5 2 .9L9 19h3l.7-2.2 2-.9 2.2.5 1.5-2.6L17 12v-2l1.4-1.8-1.5-2.6-2.2.5-2-.9L12 3z"
          transform="translate(1.5 1)"
        />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
  };
  return (
    <svg
      className="nav-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}
function Navigation({ mobile = false }: { mobile?: boolean }) {
  const url = useQuery();
  const filter = url.searchParams.get("server");
  return (
    <nav
      className={mobile ? "mobile-navigation" : "desktop-navigation"}
      aria-label={mobile ? "Mobile navigation" : "Main navigation"}
    >
      {nav.map(([to, label]) => (
        <NavLink key={to} to={to + (filter ? `?server=${filter}` : "")}>
          <span className="nav-icon-slot">
            <NavIcon name={label!} />
          </span>
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
function Header() {
  const { auth } = useContext(SessionContext);
  return (
    <div className="top-bar">
      <Link className="brand" to="/" aria-label="Tedalytics home">
        <img src={icon} alt="" />
        <strong>Tedalytics</strong>
      </Link>
      {auth?.authenticated && (
        <>
          <Navigation />
          <NavLink
            className="header-settings"
            to="/settings"
            aria-label="Settings"
          >
            <NavIcon name="Settings" />
            <span>Settings</span>
          </NavLink>
        </>
      )}
    </div>
  );
}
function SignIn() {
  const { auth, refresh } = useContext(SessionContext);
  const url = useQuery();
  const token = url.pathname === "/signup" ? url.hash.slice(1) : "";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const register = !auth?.enrolled && !!token;
  const run = async () => {
    setBusy(true);
    setError("");
    try {
      const a = await passkey(register, token);
      setCsrf(a.csrf);
      refresh();
      await router.navigate("/", { replace: true });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="auth-page">
      <img className="auth-icon" src={icon} alt="Tedalytics" />
      <h1>
        {register ? "Make Tedalytics yours." : "Your media. All together."}
      </h1>
      <p>
        {register
          ? "Create your passkey to securely access Tedalytics. Your device handles the sign-in."
          : auth?.enrolled
            ? "Sign in with your passkey."
            : "Use your private enrollment link to create your first passkey."}
      </p>
      {(register || auth?.enrolled) && (
        <button
          className="primary-button"
          disabled={busy}
          onClick={() => void run()}
        >
          {busy
            ? "Waiting for your device…"
            : register
              ? "Create my passkey"
              : "Sign in with passkey"}
        </button>
      )}
      {error && <Message error>{error}</Message>}
      <small>No passwords. Just your passkey.</small>
    </main>
  );
}
function Home() {
  const url = useQuery();
  const servers = useData<Server[]>("/servers", 15000);
  const sessions = useData<Row[]>(scoped("/activity", url), 5000);
  const stats = useData<Stats>(
    scoped("/stats", url, { days: url.searchParams.get("days") || "30" }),
    60000,
  );
  const libraries = useData<Row[]>(scoped("/libraries", url), 60000);
  const recent = useData<Row[]>(scoped("/recent", url), 60000);
  const selected = url.searchParams.get("server");
  const visible = servers.data?.filter(
    (s) => !selected || String(s.id) === selected,
  );
  return (
    <Page
      title="Now playing"
      tools={
        <span className="subtle">{visible?.length || 0} media servers</span>
      }
    >
      <div className="server-strip">
        {visible?.map((s) => (
          <button
            key={s.id}
            className="server-status"
            onClick={() => navigateQuery({ server: String(s.id) })}
          >
            <span className={`status-dot ${s.status}`} />
            <span>
              <strong>{s.name}</strong>
              <small>
                {s.status === "online"
                  ? `${s.stream_count} streaming`
                  : s.status === "offline"
                    ? `Offline · last seen ${timeAgo(s.last_success)}`
                    : "Waiting for collector"}
              </small>
            </span>
            <span className="badge">
              {s.kind === "plex" ? "Plex" : "Tedflix"}
            </span>
          </button>
        ))}
      </div>
      <Section
        title="Activity"
        tools={
          <span className="subtle">
            Sessions: {sessions.data?.length || 0} &nbsp; | &nbsp; Bandwidth:{" "}
            {(
              (sessions.data?.reduce((a, s) => a + num(s.bandwidth), 0) || 0) /
              1000
            ).toFixed(1)}{" "}
            Mbps
          </span>
        }
      >
        {sessions.error ? (
          <Message error>{sessions.error}</Message>
        ) : !sessions.data ? (
          <Skeleton kind="cards" count={2} />
        ) : sessions.data.length === 0 ? (
          <div className="empty-panel">
            No active streams
            {visible?.some((s) => s.status !== "online")
              ? " on the servers currently reachable."
              : "."}
          </div>
        ) : (
          <div className="activity-grid">
            {sessions.data.map((s) => (
              <article
                className={`activity-card${s.stale ? " stale" : ""}`}
                key={`${s.server_id}:${s.session_key}`}
              >
                <Poster
                  server={s.server_id}
                  path={
                    s.media_type === "episode"
                      ? s.grandparent_thumb || s.thumb
                      : s.thumb
                  }
                />
                <div className="activity-info">
                  <span className="badge">
                    {str(s.server_name)} ·{" "}
                    {s.stale ? "Connection lost" : str(s.state)}
                  </span>
                  <h3>{str(s.full_title || s.title)}</h3>
                  <p className="accent">{str(s.friendly_name || s.user)}</p>
                  <dl>
                    <dt>Player</dt>
                    <dd>{str(s.player)}</dd>
                    <dt>Stream</dt>
                    <dd>
                      {str(s.transcode_decision).replace(
                        "copy",
                        "Direct stream",
                      )}
                    </dd>
                    <dt>Video</dt>
                    <dd>
                      {str(
                        s.stream_video_full_resolution || s.video_resolution,
                      )}{" "}
                      · {str(s.stream_video_codec).toUpperCase()}
                    </dd>
                    <dt>Audio</dt>
                    <dd>{str(s.stream_audio_codec).toUpperCase()}</dd>
                    <dt>Bandwidth</dt>
                    <dd>{(num(s.bandwidth) / 1000).toFixed(1)} Mbps</dd>
                  </dl>
                  <progress value={num(s.progress_percent)} max={100} />
                  <small>
                    {duration(num(s.view_offset) / 1000)} /{" "}
                    {duration(num(s.duration) / 1000)}
                  </small>
                </div>
              </article>
            ))}
          </div>
        )}
      </Section>
      <Section title="Watch Statistics" tools={<DayPicker />}>
        {stats.error && <Message error>{stats.error}</Message>}
        {!stats.data && !stats.error ? (
          <Skeleton kind="cards" count={3} />
        ) : (
          <div className="metrics">
            <Metric label="Total plays" value={count(stats.data?.plays)} />
            <Metric label="Watch time" value={duration(stats.data?.duration)} />
            <Metric label="Active users" value={count(stats.data?.users)} />
          </div>
        )}
        <div className="stats-grid">
          <TopList
            title="Most Watched Movies"
            rows={stats.data?.movies}
            posters
          />
          <TopList
            title="Most Watched TV Shows"
            rows={stats.data?.shows}
            posters
          />
          <TopList title="Most Active Users" rows={stats.data?.users_top} />
          <TopList title="Most Active Platforms" rows={stats.data?.platforms} />
        </div>
      </Section>
      <Section
        title="Library Statistics"
        tools={<Link to={useLink("/libraries")}>View libraries →</Link>}
      >
        {!libraries.data && !libraries.error && <Skeleton kind="cards" />}
        <div className="library-cards">
          {libraries.data
            ?.filter((l) => num(l.is_active))
            .map((l) => (
              <article key={str(l.id)}>
                <span className="badge">{str(l.server_name)}</span>
                <h3>{str(l.section_name)}</h3>
                <strong>{count(l.count)}</strong>
                <small>
                  {l.section_type === "show"
                    ? `${count(l.child_count)} episodes`
                    : l.section_type === "artist"
                      ? `${count(l.child_count)} tracks`
                      : str(l.section_type) + "s"}
                </small>
              </article>
            ))}
        </div>
      </Section>
      <Section title="Recently Added">
        {!recent.data && !recent.error ? (
          <Skeleton kind="posters" count={6} />
        ) : recent.data?.length ? (
          <div className="poster-grid">
            {recent.data.map((r, i) => (
              <article key={`${r.server_id}:${r.rating_key}:${i}`}>
                <Poster
                  server={r.server_id}
                  path={r.thumb}
                  title={str(r.title)}
                />
                <h3>{str(r.title)}</h3>
                <small>{str(r.grandparent_title)}</small>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-panel">
            Library updates will appear after the next refresh.
          </div>
        )}
      </Section>
    </Page>
  );
}
function History() {
  const url = useQuery();
  const page = Math.max(
    0,
    Math.floor(Number(url.searchParams.get("page")) || 0),
  );
  const search = url.searchParams.get("search") || "";
  const [draft, setDraft] = useState(search);
  useEffect(() => setDraft(search), [search]);
  const path = scoped("/history", url, {
    offset: String(page * 50),
    search,
    username: url.searchParams.get("username") || "",
    media_type: url.searchParams.get("type") || "",
    ...(url.searchParams.get("user")
      ? { user_id: url.searchParams.get("user")! }
      : {}),
  });
  const data = useData<{ total: number; rows: Row[] }>(path);
  return (
    <Page
      title="History"
      tools={
        <span className="subtle">
          {data.data ? count(data.data.total) : "…"} plays
        </span>
      }
    >
      <div className="toolbar">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            navigateQuery({ search: draft, page: "" });
          }}
        >
          <HomeframeInput
            aria-label="Search playback history"
            placeholder="Search title or user…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>
        <HomeframeSelect
          aria-label="Media type"
          value={url.searchParams.get("type") || ""}
          onChange={(e) => navigateQuery({ type: e.target.value, page: "" })}
        >
          <option value="">All media</option>
          <option value="movie">Movies</option>
          <option value="episode">TV episodes</option>
          <option value="track">Music</option>
        </HomeframeSelect>
        <a
          className="button"
          href={
            "/api" +
            scoped("/export/history.csv", url, {
              search,
              media_type: url.searchParams.get("type") || "",
              username: url.searchParams.get("username") || "",
              user_id: url.searchParams.get("user") || "",
            })
          }
        >
          Export CSV
        </a>
      </div>
      {(url.searchParams.get("username") || url.searchParams.get("user")) && (
        <div className="toolbar">
          <span className="filter-chip">
            User:{" "}
            {url.searchParams.get("username") || url.searchParams.get("user")}
            <button
              aria-label="Clear user filter"
              onClick={() =>
                navigateQuery({ username: "", user: "", page: "" })
              }
            >
              ×
            </button>
          </span>
        </div>
      )}
      {!data.data && !data.error && <Skeleton count={7} />}
      {data.error && <Message error>{data.error}</Message>}
      <div className="table-wrap">
        <table className="history-table">
          <thead>
            <tr>
              {[
                "Date",
                "User",
                "Title",
                "Server",
                "Player",
                "Stream",
                "Watch time",
                "Progress",
              ].map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.data?.rows.map((r) => (
              <tr key={str(r.id)}>
                <td className="nowrap">{date(r.started)}</td>
                <td>{str(r.friendly_name || r.user)}</td>
                <td className="title-cell">
                  <Link to={`/history/${r.id}${url.search}`}>
                    {str(r.full_title || r.title) || "Unknown title"}
                  </Link>
                  <small>
                    {str(r.media_type)}
                    {r.year ? ` · ${r.year}` : ""}
                  </small>
                </td>
                <td>
                  <span className="badge">{str(r.server_name)}</span>
                </td>
                <td>
                  {str(r.player)}
                  <small>{str(r.platform)}</small>
                </td>
                <td>
                  <span
                    className={
                      "decision " +
                      str(r.transcode_decision).replaceAll(" ", "-")
                    }
                  >
                    {str(r.transcode_decision).replace("copy", "direct stream")}
                  </span>
                </td>
                <td>
                  {duration(
                    Math.max(
                      0,
                      num(r.stopped) - num(r.started) - num(r.paused_counter),
                    ),
                  )}
                </td>
                <td>
                  <progress
                    aria-label="Playback progress"
                    value={num(r.view_offset)}
                    max={num(r.duration) || 1}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.data?.rows.length === 0 && (
        <Message>No matching playback history.</Message>
      )}
      <div className="pagination">
        <button
          disabled={page === 0}
          onClick={() => navigateQuery({ page: String(page - 1) })}
        >
          ← Previous
        </button>
        <span>
          Page {page + 1} of{" "}
          {Math.max(1, Math.ceil((data.data?.total || 0) / 50))}
        </span>
        <button
          disabled={(page + 1) * 50 >= (data.data?.total || 0)}
          onClick={() => navigateQuery({ page: String(page + 1) })}
        >
          Next →
        </button>
      </div>
    </Page>
  );
}
function mediaValue(key: string, value: unknown) {
  if (key === "duration") return duration(num(value) / 1000);
  if (key.endsWith("bitrate"))
    return `${(num(value) / 1000).toLocaleString()} Mbps`;
  if (key.endsWith("_framerate") && /^\d+(\.\d+)?$/.test(str(value)))
    return `${str(value)} fps`;
  if (
    ["transcode_hw_decoding", "transcode_hw_encoding"].includes(key) &&
    [0, 1, "0", "1"].includes(value as number | string)
  )
    return num(value) ? "Yes" : "No";
  return str(value);
}
function HistoryDetail() {
  const url = useQuery();
  const id = url.pathname.split("/").at(-1);
  const data = useData<{ history: Row; metadata: Row; media_info: Row }>(
    `/history/${id}`,
  );
  const h = data.data?.history;
  return (
    <Page
      title={str(h?.full_title) || "Playback details"}
      tools={<Link to={"/history" + url.search}>← History</Link>}
    >
      {data.error && <Message error>{data.error}</Message>}
      {!h && !data.error && <Skeleton kind="page" />}
      {h && (
        <>
          <div className="detail-header">
            <Poster server={h.source_server_id} path={h.thumb} />
            <div>
              <span className="badge">{str(h.server_name)}</span>
              <h2>{str(h.full_title)}</h2>
              <p>{str(data.data?.metadata.summary)}</p>
              <dl>
                <dt>User</dt>
                <dd>{str(h.friendly_name || h.user)}</dd>
                <dt>Started</dt>
                <dd>{date(h.started)}</dd>
                <dt>Stopped</dt>
                <dd>{date(h.stopped)}</dd>
                <dt>Player</dt>
                <dd>{str(h.player)}</dd>
                <dt>Platform</dt>
                <dd>{str(h.platform)}</dd>
                <dt>Watch time</dt>
                <dd>
                  {duration(
                    num(h.stopped) - num(h.started) - num(h.paused_counter),
                  )}
                </dd>
              </dl>
            </div>
          </div>
          <Section title="Media Information">
            <dl className="media-info">
              {Object.entries(data.data?.media_info || {})
                .filter(
                  ([k, v]) =>
                    v != null &&
                    v !== "" &&
                    !["id", "source_id", "source_server_id"].includes(k),
                )
                .map(([k, v]) => (
                  <div key={k}>
                    <dt>{k.replaceAll("_", " ")}</dt>
                    <dd>{mediaValue(k, v)}</dd>
                  </div>
                ))}
            </dl>
          </Section>
        </>
      )}
    </Page>
  );
}
function Users() {
  const [reviewMatches, setReviewMatches] = useState(false);
  const url = useQuery();
  const data = useData<Row[]>(scoped("/users", url));
  const search = url.searchParams.get("search") || "";
  const [draft, setDraft] = useState(search);
  const sort = url.searchParams.get("sort") || "plays";
  useEffect(() => setDraft(search), [search]);
  const rows = data.data
    ?.filter((u) =>
      [str(u.username), ...((u.aliases as string[] | undefined) || [])].some(
        (name) => name.toLowerCase().includes(search.toLowerCase()),
      ),
    )
    .sort((a, b) =>
      sort === "username"
        ? str(a.username).localeCompare(str(b.username))
        : num(b[sort]) - num(a[sort]),
    );
  return (
    <Page
      title="Users"
      tools={
        <span className="subtle">
          {data.data ? count(data.data.length) : "…"} viewers
        </span>
      }
    >
      <details
        className="identity-review"
        onToggle={(e) => setReviewMatches(e.currentTarget.open)}
      >
        <summary>Username changes</summary>
        {reviewMatches && <ViewerMatches onChange={data.refresh} />}
      </details>
      <div className="toolbar">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            navigateQuery({ search: draft });
          }}
        >
          <HomeframeInput
            aria-label="Search users"
            placeholder="Find a username…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button>Search</button>
        </form>
        <HomeframeSelect
          aria-label="Sort users"
          value={sort}
          onChange={(e) => navigateQuery({ sort: e.target.value })}
        >
          <option value="plays">Most plays</option>
          <option value="duration">Most watch time</option>
          <option value="last_seen">Recently active</option>
          <option value="username">Username</option>
        </HomeframeSelect>
      </div>
      {data.error && <Message error>{data.error}</Message>}
      <div className="table-wrap">
        <table className="users-table">
          <thead>
            <tr>
              {["User", "Servers", "Plays", "Watch time", "Last seen"].map(
                (h) => (
                  <th key={h}>{h}</th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {rows?.map((u) => (
              <tr key={str(u.username)}>
                <td>
                  <div className="user-name">
                    <span className="avatar" aria-hidden="true">
                      {str(u.username).slice(0, 2).toUpperCase()}
                    </span>
                    <Link
                      to={
                        "/history?" +
                        new URLSearchParams({
                          username: str(u.username),
                          ...(url.searchParams.get("server")
                            ? { server: url.searchParams.get("server")! }
                            : {}),
                        })
                      }
                    >
                      {str(u.username)}
                    </Link>
                  </div>
                  {!!(u.aliases as string[] | undefined)?.length && (
                    <div className="user-aliases">
                      Previously {(u.aliases as string[]).join(" · ")}
                    </div>
                  )}
                </td>
                <td>
                  <div className="server-badges">
                    {(u.servers as Row[]).map((server) => (
                      <Link
                        className="badge"
                        key={str(server.id)}
                        to={
                          "/history?" +
                          new URLSearchParams({
                            server: str(server.id),
                            username: str(u.username),
                          })
                        }
                      >
                        {str(server.name)}
                      </Link>
                    ))}
                  </div>
                </td>
                <td>{count(u.plays)}</td>
                <td>{duration(u.duration)}</td>
                <td className="nowrap">{timeAgo(u.last_seen)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data.data && !data.error && <Skeleton count={7} />}
        {rows?.length === 0 && (
          <div className="loading-row">No matching users.</div>
        )}
      </div>
    </Page>
  );
}
function Libraries() {
  const url = useQuery();
  const historical = url.searchParams.get("historical") === "1";
  const data = useData<Row[]>(
    scoped("/libraries", url, { include_historical: String(historical) }),
  );
  return (
    <Page
      title="Libraries"
      tools={
        <HomeframeSelect
          aria-label="Library visibility"
          value={historical ? "all" : "current"}
          onChange={(e) =>
            navigateQuery({ historical: e.target.value === "all" ? "1" : "" })
          }
        >
          <option value="current">Current libraries</option>
          <option value="all">Include historical libraries</option>
        </HomeframeSelect>
      }
    >
      {data.error && <Message error>{data.error}</Message>}
      <div className="table-wrap">
        <table className="libraries-table">
          <thead>
            <tr>
              {[
                "Library",
                "Server",
                "Collection",
                "Items",
                "Seasons / albums",
                "Episodes / tracks",
              ].map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.data?.map((l) => (
              <tr key={str(l.id)}>
                <td>
                  <strong>{str(l.section_name)}</strong>
                  <small>
                    {num(l.is_active)
                      ? `Refreshed ${timeAgo(l.libraries_refreshed)}`
                      : "Historical library"}
                  </small>
                </td>
                <td>
                  <span className="badge">{str(l.server_name)}</span>
                </td>
                <td>
                  {{
                    movie: "Movies / videos",
                    show: "TV series",
                    artist: "Music",
                    clip: "Clips",
                  }[str(l.section_type)] || str(l.section_type)}
                </td>
                <td>{count(l.count)}</td>
                <td>
                  {["show", "artist"].includes(str(l.section_type))
                    ? count(l.parent_count)
                    : "—"}
                </td>
                <td>
                  {["show", "artist"].includes(str(l.section_type))
                    ? count(l.child_count)
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data.data && !data.error && <Skeleton count={5} />}
        {data.data?.length === 0 && (
          <div className="loading-row">
            No current libraries reported by this server.
          </div>
        )}
      </div>
    </Page>
  );
}
function Settings() {
  const { refresh: authRefresh } = useContext(SessionContext);
  const servers = useData<Server[]>("/servers", 10000);
  const config = useData<Row>("/settings", 10000);
  const [editing, setEditing] = useState<Server | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [passkeyName, setPasskeyName] = useState("Backup passkey");
  const trends = config.data?.trends as Row | undefined;
  const theme = useTheme();
  const addKey = async () => {
    setBusy(true);
    setMessage("");
    try {
      const result = await passkey(true, "", passkeyName);
      setCsrf(result.csrf);
      setMessage("Passkey added.");
      config.refresh();
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Page title="Settings">
      <Section title="Appearance">
        <div className="settings-card appearance-settings">
          <label htmlFor="theme">Theme</label>
          <HomeframeSelect
            id="theme"
            value={theme.preference}
            onChange={(e) => setTheme(e.target.value as Theme)}
          >
            <option value="system">System</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </HomeframeSelect>
        </div>
      </Section>
      {(servers.error || config.error) && (
        <Message error>{servers.error || config.error}</Message>
      )}
      <Section title="Media Servers">
        {!servers.data && !servers.error && <Skeleton kind="cards" />}
        <div className="server-strip">
          {servers.data?.map((s) => (
            <button
              key={s.id}
              className="server-status"
              onClick={() => setEditing(editing?.id === s.id ? null : s)}
              aria-label={`Edit ${s.name}`}
            >
              <span className={`status-dot ${s.status}`} />
              <span>
                <strong>{s.name}</strong>
                <small>
                  {s.status === "online"
                    ? `${s.stream_count} streaming`
                    : `${s.status} · ${timeAgo(s.last_success)}`}
                </small>
              </span>
              <span className="server-kind">
                {s.kind === "plex" ? "Plex" : "Tedflix"}
              </span>
            </button>
          ))}
        </div>
        <div className="table-wrap">
          <table className="settings-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Address</th>
                <th>Status</th>
                <th>History</th>
                <th>Last connected</th>
                <th>Connection</th>
              </tr>
            </thead>
            <tbody>
              {servers.data?.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.kind === "plex" ? "Plex" : "Tedflix"}</td>
                  <td>
                    <code>{s.url}</code>
                  </td>
                  <td>
                    <span className={`status-dot ${s.status}`} />
                    {s.status}
                    {s.last_error && <small>{s.last_error}</small>}
                  </td>
                  <td>{count(s.history_count)} plays</td>
                  <td>{timeAgo(s.last_success)}</td>
                  <td>
                    <button
                      onClick={() =>
                        setEditing(editing?.id === s.id ? null : s)
                      }
                    >
                      Edit / reconnect
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {editing ? (
          <ServerForm
            key={editing.id}
            server={editing}
            onSaved={() => {
              servers.refresh();
              setEditing(null);
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <ServerForm onSaved={servers.refresh} />
        )}
      </Section>
      <Section title="Playback trends">
        {!config.data && !config.error ? (
          <Skeleton />
        ) : (
          <div className="settings-card">
            <dl>
              <dt>Recording</dt>
              <dd>
                {config.data?.monitor_enabled
                  ? "Enabled"
                  : "Paused in this environment"}
              </dd>
              <dt>Storage</dt>
              <dd>Tedalytics database</dd>
              <dt>Sample interval</dt>
              <dd>{count(trends?.sample_seconds)} seconds</dd>
              <dt>Trend history</dt>
              <dd>All time</dd>
              <dt>Latest sample</dt>
              <dd>
                {trends?.last_sample
                  ? timeAgo(trends.last_sample)
                  : "Waiting for the first sample"}
              </dd>
            </dl>
          </div>
        )}
      </Section>
      <Section title="Passkeys">
        {!config.data && !config.error && <Skeleton count={2} />}
        <div className="settings-card">
          {(config.data?.passkeys as Row[] | undefined)?.map((k) => (
            <div className="passkey-row" key={str(k.credential_id)}>
              <strong>{str(k.name)}</strong>
              <span>
                Added {shortDate(k.created)} · Last used {timeAgo(k.last_used)}
              </span>
            </div>
          ))}
          <div className="toolbar">
            <HomeframeInput
              aria-label="New passkey name"
              value={passkeyName}
              onChange={(e) => setPasskeyName(e.target.value)}
            />
            <button onClick={() => void addKey()} disabled={busy}>
              {busy ? "Waiting for device…" : "Add a backup passkey"}
            </button>
            <button
              onClick={async () => {
                try {
                  await api("/auth/logout", {});
                  setCsrf("");
                  clearDataCache();
                  authRefresh();
                } catch (e) {
                  setMessage((e as Error).message);
                }
              }}
            >
              Sign out
            </button>
          </div>
          {message && <Message>{message}</Message>}
        </div>
      </Section>
      <Section title="Monitoring">
        <Link className="button" to="/logs">
          View monitoring log
        </Link>
      </Section>
    </Page>
  );
}
function ServerForm({
  onSaved,
  server,
  onCancel,
}: {
  onSaved: () => void;
  server?: Server;
  onCancel?: () => void;
}) {
  const [expanded, setExpanded] = useState(!!server),
    [name, setName] = useState(server?.name || ""),
    [kind, setKind] = useState(server?.kind || "plex"),
    [url, setUrl] = useState(server?.url || "http://"),
    [token, setToken] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  return (
    <div className="server-form">
      <button
        onClick={() => {
          setExpanded(!expanded);
          setMessage("");
          onCancel?.();
        }}
      >
        {expanded ? "Cancel" : "Add media server"}
      </button>
      {expanded && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setMessage("");
            try {
              await api(server ? `/servers/${server.id}` : "/servers", {
                name,
                kind,
                url,
                token,
              });
              setToken("");
              setExpanded(false);
              onSaved();
            } catch (e) {
              setMessage((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <label>
            Name
            <HomeframeInput
              value={name}
              required
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label>
            Type
            <HomeframeSelect
              value={kind}
              disabled={!!server}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="plex">Plex</option>
              <option value="tedflix">Tedflix / Jellyfin</option>
            </HomeframeSelect>
          </label>
          <label>
            Server URL
            <HomeframeInput
              type="url"
              value={url}
              required
              onChange={(e) => setUrl(e.target.value)}
            />
          </label>
          <label>
            {server
              ? "API token (leave blank to keep current)"
              : "Server API token"}
            <HomeframeInput
              type="password"
              value={token}
              required={!server}
              autoComplete="off"
              onChange={(e) => setToken(e.target.value)}
            />
          </label>
          <button className="primary-button" disabled={busy} type="submit">
            {busy
              ? "Checking connection…"
              : server
                ? "Verify & save connection"
                : "Connect server"}
          </button>
          {message && <Message error>{message}</Message>}
        </form>
      )}
    </div>
  );
}
function Logs() {
  const url = useQuery();
  const data = useData<Row[]>(scoped("/logs", url), 10000);
  return (
    <Page title="Monitoring Log">
      {data.error && <Message error>{data.error}</Message>}
      {!data.data && !data.error && <Skeleton count={8} />}
      <div className="table-wrap">
        <table className="logs-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Server</th>
              <th>Event</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {data.data?.map((r) => (
              <tr key={str(r.id)}>
                <td>{date(r.timestamp)}</td>
                <td>{str(r.server_name) || "Tedalytics"}</td>
                <td>{str(r.event).replaceAll("_", " ")}</td>
                <td>{str(r.message)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Page>
  );
}
